/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { RefObject } from 'react';
import * as React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import type { FieldValidator, FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';
import { LONG_DATETIME_NO_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import {
  Button,
  ContextualHelp,
  Divider,
  Grid,
  Hidden,
  IconButton,
  InlineNotification,
  Link,
  SelectField,
  SelectOption,
  TextField,
  Typography,
  Typography2,
} from '@coursera/cds-core';
import { AddIcon, ExternalLinkIcon } from '@coursera/cds-icons';
import type { ClickButton as EventDataClickButton } from '@coursera/event-pulse-types';
import TrackedButton from '@coursera/event-pulse/react/Button';
import type { Props as TrackedButtonProps } from '@coursera/event-pulse/react/Button';
import FullscreenDialog from '@coursera/event-pulse/react/FullscreenDialog';

import { useEnterpriseProperties } from 'bundles/account-profile/common/eventing/useEnterpriseProperties';
import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';
import type { SharedEventingV3Data } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/types';
import type { CmlContent } from 'bundles/cml';
import MultiSectionComboBox from 'bundles/employability-common/components/MultiSectionComboBox';
import type { MultiSectionComboBoxItem } from 'bundles/employability-common/components/MultiSectionComboBox';
import { searchMatcher } from 'bundles/employability-common/components/utils';
import { useEnableProjectEnhancements } from 'bundles/employability-common/feature-flags/hooks/useEnableProjectEnhancements';
import { getCourseHomeLinkBySlug } from 'bundles/enterprise-legacy-learner-home/utils/courseAndS12nUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { FormRichTextField, FormTextField } from 'bundles/portfolio-projects/components/FormFields';
import type { PortfolioBuilderProps } from 'bundles/portfolio-projects/components/PortfolioBuilder';
import PortfolioBuilderGuide from 'bundles/portfolio-projects/components/PortfolioBuilderGuide';
import {
  CHARACTER_LIMIT,
  ONE_MONTH,
  SIX_MONTHS,
  SMALL_CHARACTER_LIMIT,
  getPortfolioBuilderFormTranslations,
} from 'bundles/portfolio-projects/constants';
import EVENTS from 'bundles/portfolio-projects/events';
import type {
  SharedPortfolioProject_UserPortfolioProject_ArtifactsFragment as Artifact,
  SharedPortfolioProject_UserPortfolioProjectFragment as UserPortfolioProject,
} from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';
import type { Nullable } from 'bundles/portfolio-projects/types';
import {
  getCmlContentLength,
  isCmlContent,
  safeConvertToCmlContent,
} from 'bundles/portfolio-projects/utils/cmlHelpers';

import _t from 'i18n!nls/portfolio-projects';

const formControlNames = {
  linkToWork: 'linkToWork',
  summary: 'summary',
  solution: 'solution',
  approach: 'approach',
  artifacts: 'artifacts',
} as const;

type FormValues = {
  [formControlNames.linkToWork]?: string;
  [formControlNames.summary]: string;
  [formControlNames.solution]: CmlContent;
  [formControlNames.approach]: CmlContent;
  [formControlNames.artifacts]: Omit<Artifact, '__typename'>[];
};

type FieldValidatorType = string | CmlContent;

export type SharedEventTrackingData = SharedEventingV3Data & {
  product?: EventDataClickButton['product'];
};

export type PortfolioBuilderFormProps = PortfolioBuilderProps & {
  onSelectProject: (project: UserPortfolioProject) => void;
  onSave: () => void;
  onPublish: () => void;
  onHide: () => void;
  saving: boolean;
  hasFormError: boolean;
};

export type PortfolioBuilderFormRef = {
  getValues: () => FormValues | undefined;
};

function composeValidators<FieldValue>(validators: FieldValidator<FieldValue>[]): FieldValidator<FieldValue> {
  return (value, allValues, meta) =>
    validators.reduce((error, validator) => error || (validator && validator(value, allValues, meta)), undefined);
}

const TrackedButtonWrapper = ({ trackingData, children, ...restButtonProps }: TrackedButtonProps) => (
  /* I wrap the button with a TrackedDiv for visibility tracking since I cannot use component={TrackedButton} on CDS buttons. It breaks
   * eventing V3's visibility tracking, which relies on an HTML ref. Using component={TrackedButton} on a CDS button causes the ref
   * to refer to the TrackedButton component itself, *not* the underlying HTML element, which in turn results in the IntersectionObserver
   * used in `useVisibilityTracker()` to error out since we're not referencing an html element.
   * */
  <TrackedDiv withVisibilityTracking requireFullyVisible trackingName={trackingData.button.name}>
    <TrackedButton trackingData={trackingData} {...restButtonProps}>
      {children}
    </TrackedButton>
  </TrackedDiv>
);

const styles = {
  formContainer: css`
    & > * {
      margin-bottom: var(--cds-spacing-400);
    }

    // Remove inherited styes that were conflicating with cds
    input {
      margin-bottom: 0;
      border: 0;
      box-shadow: none !important;
      background: transparent;
    }

    label {
      margin-bottom: 0;
    }
  `,
  openProjectLink: css`
    margin-left: var(--cds-spacing-200);
  `,
  actions: css`
    & > div {
      display: inline-block;
    }

    & button {
      margin-right: var(--cds-spacing-200);
    }
  `,
  divider: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-150);
  `,
  selectedProject: css`
    flex-grow: 2;
  `,
  formError: css`
    display: flex !important;
    margin-bottom: var(--cds-spacing-200);
  `,
  projectTitle: css`
    font-size: 24px;
    display: block;
    margin-bottom: var(--cds-spacing-300);
  `,
  addLink: css`
    margin-top: var(--cds-spacing-200);
  `,
  projectLinksDivided: css`
    display: flex;
    gap: var(--cds-spacing-150);
    flex-direction: row;
    margin-bottom: var(--cds-spacing-200);
  `,
  projectLinksDividedTitle: css`
    width: 100%;
    max-width: 270px !important;
    flex-grow: 0;
  `,
  projectLinksDividedUrl: css`
    width: 100%;
    flex-grow: 1;
  `,
  alignedInput: css`
    display: inline-flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
  `,
  projectLinksSummary: css`
    display: flex;
    flex-direction: column;
    margin-bottom: var(--cds-spacing-200);
    gap: var(--cds-spacing-50);
  `,
  projectLinksDelete: css`
    align-self: end;
  `,
  partnerLogo: css`
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 4px;
    margin-right: var(--cds-spacing-200);
  `,
  partnerName: css`
    display: inline-flex;
  `,
  requiredLabel: css`
    display: inline;
  `,
};

const usePortfolioBuilderFormValidators = () => {
  const UrlRegex = new RegExp(
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi
  );
  const required: FieldValidator<FieldValidatorType> = (value) => {
    const isEmpty = isCmlContent(value) ? getCmlContentLength(value) <= 0 : !value;
    return isEmpty ? _t('This field is required') : undefined;
  };
  const maxLength =
    (max: number): FieldValidator<FieldValidatorType> =>
    (value) => {
      const length = isCmlContent(value) ? getCmlContentLength(value) : value?.length || 0;
      return length > max
        ? _t('This field should be #{numberOfCharacters} characters or less', {
            numberOfCharacters: max,
          })
        : undefined;
    };
  const validUrl: FieldValidator<FieldValidatorType> = (value) => {
    return value && !isCmlContent(value) && !value.match(UrlRegex)
      ? _t('Please enter a valid URL that starts with http(s)://')
      : undefined;
  };

  return { required, maxLength, validUrl } as const;
};

const PortfolioBuilderForm = forwardRef<PortfolioBuilderFormRef, PortfolioBuilderFormProps>(
  (
    {
      title,
      canOpenProjectHome,
      project,
      projectsList,
      open,
      onClose,
      onSave,
      onPublish,
      onHide,
      onSelectProject,
      saving,
      hasFormError,
    },
    ref
  ) => {
    const { Header, HeadingGroup, Content, Actions } = FullscreenDialog;
    const translations = getPortfolioBuilderFormTranslations();
    const enterpriseProperties = useEnterpriseProperties();
    const enableProjectEnhancements = useEnableProjectEnhancements();
    const courseHomeLink = getCourseHomeLinkBySlug(project?.project.slug || '/404'); // not sure if this can be used as a default route
    const { required, maxLength, validUrl } = usePortfolioBuilderFormValidators();

    const [, setQuery] = useState<string>('');
    const [shouldValidateForPublish, setShouldValidateForPublish] = useState<boolean>(false);
    const formRef = useRef<Nullable<FormApi<FormValues>>>(null);

    const linkToWorkContainerRef = useRef<HTMLDivElement>(null);
    const summaryContainerRef = useRef<HTMLDivElement>(null);
    const solutionContainerRef = useRef<HTMLDivElement>(null);
    const approachContainerRef = useRef<HTMLDivElement>(null);
    const capstoneArtifactsRef = useRef<HTMLDivElement>(null);
    const formControlContainerRefs = useMemo<Record<keyof typeof formControlNames, RefObject<HTMLDivElement>>>(
      () =>
        ({
          [formControlNames.linkToWork]: linkToWorkContainerRef,
          [formControlNames.summary]: summaryContainerRef,
          [formControlNames.solution]: solutionContainerRef,
          [formControlNames.approach]: approachContainerRef,
          [formControlNames.artifacts]: capstoneArtifactsRef,
        } as const),
      [linkToWorkContainerRef, summaryContainerRef, solutionContainerRef, approachContainerRef]
    );
    const initialValues = useMemo<FormValues>(() => {
      const artifacts = project?.artifacts ?? [];
      return {
        [formControlNames.linkToWork]: project?.linkToWork || '',
        [formControlNames.summary]: project?.summary || '',
        [formControlNames.solution]: safeConvertToCmlContent(project?.solution),
        [formControlNames.approach]: safeConvertToCmlContent(project?.approach),
        [formControlNames.artifacts]:
          artifacts.length > 0
            ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
              artifacts.map(({ __typename, ...restProps }) => restProps)
            : // not including the other types because only these 2 are required. if I did include them, it would butcher saving
              [{ link: null, type: null }],
      };
    }, [project]);

    const sharedTrackingData: SharedEventTrackingData = {
      ...(project?.project.id && {
        product: { id: project.project.id || '' },
      }),
      candidate: {
        // This is ok because other users cannot trigger your portfolio builder, so we know the candidate id will be the user's external id
        externalId: user.get().external_id,
      },
      enterprise: {
        organizationSlug: enterpriseProperties?.organizationSlug,
        programSlug: enterpriseProperties?.programSlug,
      },
    };

    const contextualHelps = {
      projectLink: translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_CONTEXTUAL_HELP,
      summary: translations.PORTFOLIO_BUILDER_FORM_SUMMARY_CONTEXTUAL_HELP,
      solution: translations.PORTFOLIO_BUILDER_FORM_SOLUTION_CONTEXTUAL_HELP,
      approach: translations.PORTFOLIO_BUILDER_FORM_APPROACH_CONTEXTUAL_HELP,
    };

    const isPublishValidationEnabled = shouldValidateForPublish || project?.isPublished;
    const showSelectProject = !enableProjectEnhancements || !!projectsList;

    const triggerFormSubmit = () => {
      formRef.current?.submit();
      formRef.current?.mutators.focusError();
    };

    const selectProject = (id: string) => {
      const selectedProject = projectsList?.find((p) => p.project.id === id);
      if (selectedProject) {
        onSelectProject(selectedProject);
      }
    };

    const handleSelectProject = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const id = e.target.value as string;
      selectProject(id);
    };

    const onSubmit = useCallback<() => $TSFixMe>(() => {
      onSave();
    }, [onSave]);

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => formRef.current?.getState().values,
      }),
      [formRef]
    );

    useEffect(() => {
      if (!shouldValidateForPublish) {
        return;
      }

      formRef.current?.mutators.validate();
      formRef.current?.mutators.focusError();
      setShouldValidateForPublish(false);

      if (!formRef.current?.getState().invalid && formRef.current?.getState().values) {
        onPublish();
      }
    }, [shouldValidateForPublish, onPublish]);

    const renderedContextualHelp = (contextualHelpText: string) => (labelComponent: React.ReactElement) =>
      (
        <ContextualHelp
          label={labelComponent}
          helpToggleProps={{
            'aria-label': _t('Additional information'),
          }}
        >
          <Typography2 component="p" variant="bodyPrimary">
            {contextualHelpText}
          </Typography2>
        </ContextualHelp>
      );

    const renderSelectedProject = () => {
      if (!projectsList) {
        return (
          <TextField
            label={translations.PORTFOLIO_BUILDER_FORM_AUTO_POPULATED_PROJECT_LABEL}
            value={project?.project.title || ''}
            readOnly
            fullWidth
          />
        );
      }

      if (!enableProjectEnhancements) {
        return (
          <SelectField
            label={translations.PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_LABEL}
            placeholder={translations.PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_LABEL}
            fullWidth
            onChange={handleSelectProject}
            value={project?.project.id}
          >
            {projectsList.map((p) => (
              <SelectOption key={`project-${p.project.id}`} value={p.project.id}>
                {p.project.title}
              </SelectOption>
            ))}
          </SelectField>
        );
      }

      const formatComboBoxItem = (label: string, value: string) => {
        return {
          key: value,
          label,
          onClick: (closePopover?: () => void) => {
            selectProject(value);
            closePopover?.();
          },
          match: (term?: string) => {
            return searchMatcher({ label }, term);
          },
          content: () => <SelectOption value={value}>{label}</SelectOption>,
        };
      };

      const completedInLastMonth = _t('Completed in the last month');
      const completedInLast6Months = _t('Completed in the last 6 months');
      const completedOver6MonthsAgo = _t('Completed over 6 months ago');
      const currentProjectsLabel = _t('Current projects');
      const segmentedProjects: { [x: string]: MultiSectionComboBoxItem[] } = {
        [completedInLastMonth]: [],
        [completedInLast6Months]: [],
        [completedOver6MonthsAgo]: [],
        [currentProjectsLabel]: [],
      };
      const today = new Date().valueOf();
      const inLastMonth = today - ONE_MONTH;
      const inLastSixMonths = today - SIX_MONTHS;

      (projectsList ?? []).forEach((proj) => {
        const {
          completed,
          project: { title: pTitle, id },
        } = proj;

        // @ts-expect-error DateTime: string
        const completedDate = new Date(completed).valueOf();

        if (!completed) {
          segmentedProjects[currentProjectsLabel].push(formatComboBoxItem(pTitle, id));
        } else if (completedDate > inLastMonth) {
          segmentedProjects[completedInLastMonth].push(formatComboBoxItem(pTitle, id));
        } else if (completedDate > inLastSixMonths) {
          segmentedProjects[completedInLast6Months].push(formatComboBoxItem(pTitle, id));
        } else {
          segmentedProjects[completedOver6MonthsAgo].push(formatComboBoxItem(pTitle, id));
        }
      });

      for (const key of Object.keys(segmentedProjects)) {
        if (!segmentedProjects[key].length) {
          delete segmentedProjects[key];
        }
      }

      return (
        <MultiSectionComboBox
          activePlaceholder={translations.PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_PLACEHOLDER}
          items={segmentedProjects}
          inactivePlaceholder={project?.project.title || translations.PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_LABEL}
          fullWidth={true}
          onSearch={(searchQuery) => setQuery(searchQuery ?? '')}
        />
      );
    };

    return (
      <FullscreenDialog
        // This key is necessary because the PortfolioBuilder dialog is shared across many different projects. By including this key,
        // we ensure that when a user closes one edit dialog and then tries to open an add dialog, for instance, visibility tracking will
        // be run again for that new opened dialog.
        key={project?.id}
        trackingData={{
          modal: {
            name: project?.isPublished ? EVENTS.portfolioEditProjectsDialog : EVENTS.portfolioAddProjectsDialog,
          },
          ...sharedTrackingData,
        }}
        aria-describedby={title}
        open={open}
        onClose={onClose}
        sticky="actions"
      >
        <Header slot1={<Typography variant="h2semibold">{title}</Typography>} />
        <HeadingGroup
          heading={translations.PORTFOLIO_BUILDER_FORM_HEADING}
          supportText={translations.PORTFOLIO_BUILDER_FORM_HEADING_SUPPORT_TEXT}
        />

        <Content>
          {enableProjectEnhancements && (
            <Grid container direction="column" spacing={16}>
              {project?.isPublished && (
                <Grid item direction="row">
                  <Imgix
                    width={80}
                    height={30}
                    // @ts-expect-error URL: string
                    src={project?.project?.partner?.logo}
                    alt={`${project?.project?.partner?.name} logo`}
                    css={styles.partnerLogo}
                  />

                  <Typography2 component="p" css={styles.partnerName} variant="bodySecondary" color="supportText">
                    {project?.project?.partner?.name}
                  </Typography2>
                </Grid>
              )}

              <Grid item>
                <Link
                  css={styles.projectTitle}
                  href={courseHomeLink}
                  icon={<ExternalLinkIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {project?.project?.title}
                </Link>
              </Grid>
            </Grid>
          )}

          <Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            mutators={{
              ...arrayMutators,
              validate(_, state, { changeValue }) {
                Object.values(state.fields).forEach(({ name, blur }) => {
                  changeValue(state, name, (n) => n);
                  blur();
                });
              },
              focusError(_, state) {
                const firstError = Object.keys(state.formState.errors || {})[0] as keyof typeof formControlNames;
                if (firstError) {
                  const el = formControlContainerRefs[firstError];
                  el.current?.scrollIntoView({ block: 'nearest' });
                }
              },
            }}
          >
            {({ handleSubmit, form }) => {
              const { push } = form.mutators;
              formRef.current = form;

              const handleAddLinkClick = () => {
                push(formControlNames.artifacts);
              };

              return (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={32} justifyContent="space-between">
                    <Grid item xs={12} sm={12} md={7} css={styles.formContainer}>
                      {showSelectProject &&
                        (canOpenProjectHome ? (
                          <Grid container item alignItems="flex-end">
                            <Grid item css={styles.selectedProject}>
                              {renderSelectedProject()}
                            </Grid>
                            <Grid item>
                              <TrackedButtonWrapper
                                component="a"
                                // @ts-expect-error - href doesn't work well with v3 tracked button type
                                href={courseHomeLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                trackingData={{
                                  button: {
                                    name: EVENTS.portfolioBuilderOpenCourse,
                                    linkType: 'internal',
                                    linkURL: courseHomeLink,
                                  },
                                  ...sharedTrackingData,
                                }}
                                variant="ghost"
                                icon={<ExternalLinkIcon />}
                                iconPosition="after"
                                css={[styles.openProjectLink]}
                                disabled={project == null}
                              >
                                {_t('Open')}
                              </TrackedButtonWrapper>
                            </Grid>
                          </Grid>
                        ) : (
                          <Grid item xs={12}>
                            {renderSelectedProject()}
                          </Grid>
                        ))}

                      <Grid
                        item
                        xs={12}
                        ref={
                          enableProjectEnhancements
                            ? formControlContainerRefs[formControlNames.artifacts]
                            : formControlContainerRefs[formControlNames.linkToWork]
                        }
                      >
                        {enableProjectEnhancements ? (
                          <>
                            <div css={styles.projectLinksSummary}>
                              {renderedContextualHelp(contextualHelps.projectLink)(
                                <RequiredFieldLabel
                                  disableRequiredGatingLogic
                                  labelComponent={
                                    <Typography2 component="h3" variant="subtitleMedium" css={styles.requiredLabel}>
                                      {translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_LABEL}
                                    </Typography2>
                                  }
                                />
                              )}

                              <Typography2 component="p" variant="bodySecondary">
                                {translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_SUPPORT_TEXT}
                              </Typography2>
                            </div>

                            <FieldArray name={formControlNames.artifacts}>
                              {({ fields }) =>
                                fields.map((name, index) => {
                                  const linkTitleFieldName = `${name}type`;
                                  const linkUrlFieldName = `${name}link`;

                                  const handleRemoveArtifactClick = () => {
                                    if (fields.length === 1 && index === 0) {
                                      // We don't fully remove it because we want the input boxes to remain so we can provide
                                      // field validation if the user tries to save and they are empty
                                      fields.update(index, { type: null, link: null });
                                    } else {
                                      fields.remove(index);
                                    }
                                  };

                                  return (
                                    <div key={name} css={styles.projectLinksDivided}>
                                      <Field
                                        css={[styles.projectLinksDividedTitle, styles.alignedInput]}
                                        name={linkTitleFieldName}
                                        component={FormTextField}
                                        label={translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_TITLE_LABEL}
                                        placeholder={
                                          translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_TITLE_PLACEHOLDER
                                        }
                                        type="text"
                                        validate={
                                          isPublishValidationEnabled ? composeValidators([required]) : undefined
                                        }
                                      />
                                      <Field
                                        css={[styles.projectLinksDividedUrl, styles.alignedInput]}
                                        name={linkUrlFieldName}
                                        component={FormTextField}
                                        label={translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_URL_LABEL}
                                        placeholder={
                                          translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_URL_PLACEHOLDER
                                        }
                                        type="url"
                                        validate={composeValidators([
                                          ...(isPublishValidationEnabled ? [required] : []),
                                          validUrl,
                                        ])}
                                      />
                                      {(project?.isPublished || index > 0) && (
                                        <IconButton
                                          css={styles.projectLinksDelete}
                                          variant="ghost"
                                          intent="delete"
                                          onClick={handleRemoveArtifactClick}
                                        />
                                      )}
                                    </div>
                                  );
                                })
                              }
                            </FieldArray>

                            <Button
                              size="small"
                              variant="secondary"
                              icon={<AddIcon />}
                              iconPosition="before"
                              onClick={handleAddLinkClick}
                              css={styles.addLink}
                            >
                              {translations.PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_ADD_LABEL}
                            </Button>
                          </>
                        ) : (
                          <Field
                            name={formControlNames.linkToWork}
                            component={FormTextField}
                            label={translations.PORTFOLIO_BUILDER_FORM_PROJECT_LINK_LABEL}
                            supportText={translations.PORTFOLIO_BUILDER_FORM_PROJECT_LINK_SUPPORT_TEXT}
                            placeholder={translations.PORTFOLIO_BUILDER_FORM_PROJECT_LINK_PLACEHOLDER}
                            type="url"
                            fullWidth
                            renderLabel={renderedContextualHelp(contextualHelps.projectLink)}
                            validate={composeValidators([
                              ...(!enableProjectEnhancements && isPublishValidationEnabled ? [required] : []),
                              validUrl,
                            ])}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} ref={formControlContainerRefs[formControlNames.summary]}>
                        <Field
                          name={formControlNames.summary}
                          component={FormTextField}
                          label={translations.PORTFOLIO_BUILDER_FORM_SUMMARY_LABEL}
                          supportText={translations.PORTFOLIO_BUILDER_FORM_SUMMARY_SUPPORT_TEXT}
                          placeholder={translations.PORTFOLIO_BUILDER_FORM_SUMMARY_PLACEHOLDER}
                          characterLimit={SMALL_CHARACTER_LIMIT}
                          renderLabel={renderedContextualHelp(contextualHelps.summary)}
                          validate={composeValidators([
                            ...(!enableProjectEnhancements && isPublishValidationEnabled ? [required] : []),
                            maxLength(SMALL_CHARACTER_LIMIT),
                          ])}
                        />
                      </Grid>
                      <Grid item xs={12} ref={formControlContainerRefs[formControlNames.solution]}>
                        <Field
                          name={formControlNames.solution}
                          component={FormRichTextField}
                          label={translations.PORTFOLIO_BUILDER_FORM_SOLUTION_LABEL}
                          ariaLabel={translations.PORTFOLIO_BUILDER_FORM_SOLUTION_LABEL}
                          supportText={translations.PORTFOLIO_BUILDER_FORM_SOLUTION_SUPPORT_TEXT}
                          placeholder={translations.PORTFOLIO_BUILDER_FORM_SOLUTION_PLACEHOLDER}
                          characterLimit={CHARACTER_LIMIT}
                          renderLabel={renderedContextualHelp(contextualHelps.solution)}
                          validate={composeValidators([
                            ...(!enableProjectEnhancements && isPublishValidationEnabled ? [required] : []),
                            maxLength(CHARACTER_LIMIT),
                          ])}
                        />
                      </Grid>
                      <Grid item xs={12} ref={formControlContainerRefs[formControlNames.approach]}>
                        <Field
                          name={formControlNames.approach}
                          component={FormRichTextField}
                          label={translations.PORTFOLIO_BUILDER_FORM_APPROACH_LABEL}
                          ariaLabel={translations.PORTFOLIO_BUILDER_FORM_APPROACH_LABEL}
                          supportText={translations.PORTFOLIO_BUILDER_FORM_APPROACH_SUPPORT_TEXT}
                          placeholder={translations.PORTFOLIO_BUILDER_FORM_APPROACH_PLACEHOLDER}
                          characterLimit={CHARACTER_LIMIT}
                          renderLabel={renderedContextualHelp(contextualHelps.approach)}
                          validate={composeValidators([
                            ...(!enableProjectEnhancements && isPublishValidationEnabled ? [required] : []),
                            maxLength(CHARACTER_LIMIT),
                          ])}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Hidden smDown>
                        <PortfolioBuilderGuide trackingData={sharedTrackingData} />
                      </Hidden>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Form>
        </Content>

        <Actions css={styles.actions}>
          {hasFormError && (
            <InlineNotification severity="error" css={styles.formError}>
              {translations.PORTFOLIO_BUILDER_FORM_ERROR_MESSAGE}
            </InlineNotification>
          )}
          {project?.isPublished ? (
            <>
              <TrackedButtonWrapper
                trackingData={{
                  button: {
                    name: EVENTS.portfolioBuilderSave,
                  },
                  ...sharedTrackingData,
                }}
                variant="primary"
                size="medium"
                loading={saving}
                onClick={triggerFormSubmit}
              >
                {translations.PORTFOLIO_BUILDER_FORM_SAVE_CHANGES_LABEL}
              </TrackedButtonWrapper>

              <TrackedButtonWrapper
                trackingData={{
                  button: {
                    name: EVENTS.portfolioBuilderHide,
                  },
                  ...sharedTrackingData,
                }}
                variant="secondary"
                size="medium"
                disabled={saving}
                onClick={onHide}
              >
                {translations.PORTFOLIO_BUILDER_FORM_UNPUBLISH_LABEL}
              </TrackedButtonWrapper>
            </>
          ) : (
            <>
              <TrackedButtonWrapper
                trackingData={{
                  button: {
                    name: EVENTS.portfolioBuilderPublish,
                  },
                  ...sharedTrackingData,
                }}
                variant="primary"
                size="medium"
                disabled={saving}
                onClick={() => setShouldValidateForPublish(true)}
              >
                {translations.PORTFOLIO_BUILDER_FORM_PUBLISH_LABEL}
              </TrackedButtonWrapper>

              <TrackedButtonWrapper
                trackingData={{
                  button: {
                    name: EVENTS.portfolioBuilderSaveDraft,
                  },
                  ...sharedTrackingData,
                }}
                variant="secondary"
                size="medium"
                loading={saving}
                onClick={triggerFormSubmit}
              >
                {translations.PORTFOLIO_BUILDER_FORM_SAVE_DRAFT_LABEL}
              </TrackedButtonWrapper>
            </>
          )}
          <Divider css={styles.divider} />
          {project?.lastUpdated && (
            <Typography2 component="p" variant="actionSecondary" color="supportText">
              {saving ? (
                translations.PORTFOLIO_BUILDER_FORM_SAVING_LABEL
              ) : (
                <FormattedMessage
                  message={translations.PORTFOLIO_BUILDER_FORM_LAST_SAVED_LABEL}
                  date={formatDateTimeDisplay(project.lastUpdated, LONG_DATETIME_NO_YEAR_DISPLAY)}
                />
              )}
            </Typography2>
          )}
        </Actions>
      </FullscreenDialog>
    );
  }
);

export default PortfolioBuilderForm;
